import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, Loader2, Clock, Zap, AlertTriangle, ArrowRight } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDistanceToNow } from "date-fns";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import {
  describeAiError,
  parseApiError,
  type FriendlyAiError,
} from "@/lib/ai-error";

type EntityType = "lead" | "client";

// Cached summaries older than this are surfaced as "stale" with a regenerate CTA.
const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

interface CatchMeUpButtonProps {
  entityType: EntityType;
  entityId: string;
  eventCount: number;
  size?: "sm" | "default";
}

interface BalanceResponse {
  enabled: boolean;
  catchMeUpEnabled?: boolean;
  monthUsageCents: number;
  monthGiftedAllowanceCents: number;
  giftedRemainingCents: number;
  paidRemainingCents: number;
  overageCapCents: number | null;
  // -1 is the server's "unlimited / no cap" sentinel; anything >=0 is real remaining cents.
  overageRemainingCents: number;
}

interface CatchMeUpResponse {
  bullets: string[];
  eventCount: number;
  costCents: number;
  modelKey: string;
  cached?: boolean;
  cachedAt?: string;
}

interface CatchMeUpStatusResponse {
  cached: boolean;
  cachedAt?: string;
  eventCount?: number;
}

// A cached row is considered "fresh" if it was generated within this window
// AND the underlying activity count hasn't grown since. Outside that window
// it's "stale" — the rep should know to regenerate before relying on it.
const FRESH_WINDOW_MS = 24 * 60 * 60 * 1000;

export function CatchMeUpButton({
  entityType,
  entityId,
  eventCount,
  size = "sm",
}: CatchMeUpButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bullets, setBullets] = useState<string[] | null>(null);
  const [error, setError] = useState<FriendlyAiError | null>(null);
  const [cached, setCached] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [, setNowTick] = useState(0);
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { role } = useEffectivePermissions();
  const isAdmin = role === "admin";

  // Re-render the relative timestamp every 30s while the popover is open
  // so "Updated 2 minutes ago" doesn't go stale on the screen.
  useEffect(() => {
    if (!open || !cachedAt) return;
    const id = setInterval(() => setNowTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [open, cachedAt]);

  const balance = useQuery<BalanceResponse>({
    queryKey: ["/api/ai/balance"],
  });

  const aiOff = balance.data && !balance.data.enabled;
  const featureOff = balance.data && balance.data.catchMeUpEnabled === false;
  const tooFew = eventCount < 2;

  // Read-only status query — never triggers an AI call. Lets us show a
  // freshness dot on the button without the rep having to open the popover.
  const status = useQuery<CatchMeUpStatusResponse>({
    queryKey: ["/api/ai/catch-me-up/status", entityType, entityId],
    enabled: !aiOff && !featureOff && !tooFew && !!entityId,
    staleTime: 60_000,
  });

  const freshness: "fresh" | "stale" | "none" = (() => {
    const data = status.data;
    if (!data?.cached || !data.cachedAt) return "none";
    const t = new Date(data.cachedAt).getTime();
    if (Number.isNaN(t)) return "none";
    const ageMs = Date.now() - t;
    const recent = ageMs >= 0 && ageMs < FRESH_WINDOW_MS;
    const eventsMatch =
      typeof data.eventCount === "number" ? data.eventCount >= eventCount : true;
    return recent && eventsMatch ? "fresh" : "stale";
  })();

  // Out of budget = no gifted, no paid, AND (no overage cap remaining OR cap is 0).
  // overageRemainingCents = -1 is the server's sentinel for "no cap / unlimited".
  const outOfBudget =
    !!balance.data &&
    balance.data.giftedRemainingCents <= 0 &&
    balance.data.paidRemainingCents <= 0 &&
    balance.data.overageRemainingCents !== -1 &&
    balance.data.overageRemainingCents <= 0;

  const disabled = !!aiOff || !!featureOff || tooFew || outOfBudget;

  const tooltip = aiOff
    ? "AI is turned off for this workspace."
    : featureOff
    ? "Catch me up has been turned off by your admin."
    : outOfBudget
    ? "Out of AI credits. Ask your admin to raise the monthly cap."
    : tooFew
    ? "Need at least 2 logged events to summarize."
    : "Summarize the full activity history into 3 bullets.";

  async function run(force = false) {
    setLoading(true);
    setError(null);
    setBullets(null);
    setCached(false);
    setCachedAt(null);
    try {
      const res = await apiRequest("POST", "/api/ai/catch-me-up", {
        entityType,
        entityId,
        force,
      });
      const data: CatchMeUpResponse = await res.json();
      setBullets(data.bullets);
      setCached(!!data.cached);
      setCachedAt(data.cached ? data.cachedAt ?? null : null);
      // Refresh the badge dot — the cache row was just (re)written or read.
      queryClient.invalidateQueries({
        queryKey: ["/api/ai/catch-me-up/status", entityType, entityId],
      });
      toast({
        title: "Caught up",
        description: data.cached
          ? `Cached summary of ${data.eventCount} events.`
          : `Summarized ${data.eventCount} events (~${data.costCents}¢).`,
      });
    } catch (err: unknown) {
      const parsed = parseApiError(err);
      const friendly = describeAiError(parsed.code, parsed.message, {
        isAdmin,
        defaultTitle: "Catch me up failed",
        defaultMessage: "Something went wrong while summarizing the history.",
      });
      setError(friendly);
      toast({
        title: friendly.title,
        description: friendly.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  function handleOpen(next: boolean) {
    setOpen(next);
    if (next && !bullets && !loading && !disabled) {
      run();
    }
  }

  function handleAction(href: string) {
    setOpen(false);
    navigate(href);
  }

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size}
          disabled={disabled}
          title={
            disabled
              ? tooltip
              : freshness === "fresh"
              ? `${tooltip} (cached summary is fresh)`
              : freshness === "stale"
              ? `${tooltip} (cached summary may be out of date)`
              : tooltip
          }
          className="relative"
          data-testid="button-catch-me-up"
        >
          <Sparkles className="h-4 w-4 mr-1.5" />
          Catch me up
          {!disabled && freshness !== "none" && (
            <span
              className={
                "ml-2 inline-block h-2 w-2 rounded-full " +
                (freshness === "fresh"
                  ? "bg-emerald-500"
                  : "bg-amber-500")
              }
              aria-label={
                freshness === "fresh"
                  ? "Cached summary is fresh"
                  : "Cached summary may be out of date"
              }
              data-testid={`indicator-catch-me-up-${freshness}`}
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">AI activity summary</div>
            {!loading && bullets && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => run(true)}
                data-testid="button-catch-me-up-refresh"
              >
                Regenerate
              </Button>
            )}
          </div>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reading the full history…
            </div>
          )}

          {!loading && error && (
            <div
              className="space-y-2 py-2"
              data-testid="catch-me-up-error"
            >
              <div
                className="text-sm font-medium text-destructive"
                data-testid="text-catch-me-up-error-title"
              >
                {error.title}
              </div>
              <div
                className="text-sm text-muted-foreground"
                data-testid="text-catch-me-up-error"
              >
                {error.message}
              </div>
              {error.hint && (
                <div
                  className="text-xs text-muted-foreground"
                  data-testid="text-catch-me-up-error-hint"
                >
                  {error.hint}
                </div>
              )}
              {error.action && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => handleAction(error.action!.href)}
                  data-testid="button-catch-me-up-error-action"
                >
                  {error.action.label}
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              )}
            </div>
          )}

          {!loading && bullets && (
            <>
              <ul className="space-y-2 text-sm" data-testid="list-catch-me-up-bullets">
                {bullets.map((b, i) => (
                  <li
                    key={i}
                    className="flex gap-2"
                    data-testid={`text-catch-me-up-bullet-${i}`}
                  >
                    <span className="text-muted-foreground">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {cached ? (() => {
                const d = cachedAt ? new Date(cachedAt) : null;
                const validDate = d && !Number.isNaN(d.getTime()) ? d : null;
                const ageMs = validDate ? Date.now() - validDate.getTime() : 0;
                const isStale = !!validDate && ageMs > STALE_THRESHOLD_MS;
                const relative = validDate
                  ? formatDistanceToNow(validDate, { addSuffix: true })
                  : null;

                if (isStale) {
                  return (
                    <button
                      type="button"
                      onClick={() => run(true)}
                      className="flex w-full items-center gap-1.5 text-xs text-amber-700 dark:text-amber-400 rounded-md px-1 py-1 -mx-1 hover-elevate active-elevate-2 text-left"
                      data-testid="button-catch-me-up-stale"
                      title={
                        relative
                          ? `Cached summary from ${relative}. Click to regenerate.`
                          : "Cached summary is stale. Click to regenerate."
                      }
                    >
                      <AlertTriangle className="h-3 w-3" />
                      <span>
                        {relative
                          ? `Updated ${relative} — Stale, regenerate?`
                          : "Stale — regenerate?"}
                      </span>
                      <span className="ml-auto inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
                        Stale
                      </span>
                    </button>
                  );
                }

                return (
                  <div
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                    data-testid="text-catch-me-up-cached-at"
                  >
                    <Clock className="h-3 w-3" />
                    <span>
                      {relative ? `Updated ${relative}` : "Updated recently"}
                    </span>
                    <span className="ml-auto inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Cached
                    </span>
                  </div>
                );
              })() : (
                <div
                  className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  data-testid="text-catch-me-up-fresh"
                >
                  <Zap className="h-3 w-3 text-emerald-500" />
                  <span>Just now</span>
                  <span className="ml-auto inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                    Fresh
                  </span>
                </div>
              )}
            </>
          )}

          <div className="text-xs text-muted-foreground pt-1 border-t">
            Summary is generated by AI from {eventCount} logged events. Always verify
            details before acting on them.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
